import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KiteInput, KiteAlert } from '@kite/react-kite';
import { useParams } from 'react-router-dom';
import { useQueryData, useGetTcoLocations } from 'hooks';
import { useGetTcoUnderlayProducts } from 'hooks/apiHooks/tcoUnderlayProducts/useGetUnderlayProducts';
import SameUnderlaySectionCard, {
  UnderlayFormData,
} from './ApplySameUnderlayLocationCard';
import { ITcoLocations, TcoUnderlayProduct } from 'types/TcoProducts';

export interface IApplySameUnderlayProps {
  onEditLocation: (updated: ITcoLocations, original: ITcoLocations) => void;
  term: string;
  noOfSites?: string;
}

const MAX_UNDERLAYS = 2;

const ApplySameUnderlayToAll: React.FC<IApplySameUnderlayProps> = ({
  onEditLocation,
  term,
}) => {
  const { estimateId = '' } = useParams<{ estimateId: string }>();
  const { userId } = useQueryData();

  const { data: tcoLocations = [], isLoading: locationsLoading } =
    useGetTcoLocations({ params: { estimateId } });

  const {
    data: rawUnderlayProducts = [],
    isLoading: isLoadingUnderlayProducts,
  } = useGetTcoUnderlayProducts();

  const underlayProducts = useMemo<TcoUnderlayProduct[]>(
    () =>
      rawUnderlayProducts.map((p) => ({
        ...p,
        description: p.description || '',
        prices: (p as any).prices || [],
      })),
    [rawUnderlayProducts]
  );

  const [underlayForms, setUnderlayForms] = useState<UnderlayFormData[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState(false);
  const [relate, setRelate] = useState(false);
  const [noOfSites, setNoOfSites] = useState('');

  useEffect(() => {
    if (tcoLocations.length > 0) {
      const original = tcoLocations[0];
      const seeded: UnderlayFormData[] = original.underlaySelections.map(
        (sel) => ({
          id: sel.id,
          underlayId: sel.underlayId,
          spectrumPrice: sel.spectrumPrice.toFixed(2),
          diyPrice: sel.diyPrice.toFixed(2),
        })
      );
      setUnderlayForms(seeded);
      setNoOfSites((prev) => (prev === '' ? String(original.noOfSites) : prev));
    }
  }, [tcoLocations]);

  const handleAddUnderlay = useCallback(() => {
    const first = underlayForms[0];

    const isFirstComplete =
      first &&
      first.underlayId.trim() !== '' &&
      first.spectrumPrice.trim() !== '' &&
      first.diyPrice.trim() !== '';

    if (underlayForms.length >= 1 && !isFirstComplete) {
      setFormErrors((prev) => ({
        ...prev,
        global: 'Please complete the first underlay before adding another.',
      }));
      setGlobalError(true);
      return;
    }

    if (underlayForms.length < MAX_UNDERLAYS) {
      setUnderlayForms((f) => [
        ...f,
        { underlayId: '', spectrumPrice: '', diyPrice: '' },
      ]);
      setGlobalError(false);
    }
  }, [underlayForms]);

  const handleDeleteUnderlay = useCallback((i: number) => {
    setUnderlayForms((prev) => {
      const updated = prev.filter((_, idx) => idx !== i);

      const first = updated[0];
      const isFirstComplete =
        first &&
        first.underlayId.trim() !== '' &&
        first.spectrumPrice.trim() !== '' &&
        first.diyPrice.trim() !== '';

      if (updated.length === 0 || isFirstComplete) {
        setFormErrors((f) => ({ ...f, global: '' }));
        setGlobalError(false);
      }

      return updated;
    });

    setRelate(true);
  }, []);

  const handleUnderlayChange = useCallback(
    (index: number, field: keyof UnderlayFormData, value: string) => {
      let v = value;
      if ((field === 'spectrumPrice' || field === 'diyPrice') && value) {
        const [int, dec] = value.split('.');
        if (dec?.length > 2) {
          v = `${int}.${dec.slice(0, 2)}`;
        }
      }

      setUnderlayForms((prev) => {
        const copy = [...prev];
        copy[index] = { ...copy[index], [field]: v };

        const first = copy[0];
        const isFirstComplete =
          first &&
          first.underlayId.trim() !== '' &&
          first.spectrumPrice.trim() !== '' &&
          first.diyPrice.trim() !== '';

        if (isFirstComplete) {
          setFormErrors((f) => ({ ...f, global: '' }));
          setGlobalError(false);
        }

        return copy;
      });

      setFormErrors((f) => ({ ...f, [`${field}_${index}`]: '', global: '' }));
      setRelate(true);
    },
    []
  );

  const handleNoOfSitesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (value === '') {
        setNoOfSites('');
        setFormErrors((prev) => ({ ...prev, noOfSites: '' }));
        setGlobalError(false);
        return;
      }

      if (/^[0-9]+$/.test(value)) {
        setNoOfSites(value);

        setFormErrors((prev) => {
          const next = { ...prev };
          if (value === '0') {
            next.noOfSites = 'Minimum 1 required';
          } else {
            delete next.noOfSites;
          }
          return next;
        });

        setGlobalError(false);
        setRelate(true);
      }
    },
    []
  );

  const handleNoOfSitesKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === '.' || e.key.toLowerCase() === 'e') {
        e.preventDefault();
      }
    },
    []
  );

  const handleAddLocation = useCallback(() => {
    const original = tcoLocations[0];
    if (!original) return;

    const formatPrice = (s: string) => {
      const n = parseFloat(s);
      return isNaN(n) ? 0 : +n.toFixed(2);
    };

    const filteredForms = underlayForms.filter(
      (form) => form.underlayId.trim() !== ''
    );

    const updated: ITcoLocations = {
      ...original,
      underlaySelections: filteredForms.map((form) => ({
        id: form.id || '',
        locationId: original.id,
        underlayId: form.underlayId,
        term,
        spectrumPrice: formatPrice(form.spectrumPrice),
        diyPrice: formatPrice(form.diyPrice),
        updatedBy: userId,
        createdAt: original.createdAt,
        updatedAt: original.updatedAt,
      })),
      updatedBy: userId,
      noOfSites: Number(noOfSites),
    };

    onEditLocation(updated, original);
  }, [tcoLocations, underlayForms, term, noOfSites, userId, onEditLocation]);

  useEffect(() => {
    if (relate) {
      handleAddLocation();
      setRelate(false);
    }
  }, [relate, handleAddLocation]);

  return (
    <>
      <div className="no-of-sites-container-sameUnderlay">
        <label htmlFor="noOfSitesInput" className="kite-label">
          #Sites
        </label>
        <KiteInput
          id="noOfSitesInput"
          name="noOfSites"
          value={noOfSites}
          onChange={handleNoOfSitesChange}
          placeholder="Enter Number"
          type="number"
          min="1"
          maxWidth="none"
          errorMessage={formErrors.noOfSites}
          onKeyDown={handleNoOfSitesKeyDown}
        />
      </div>

      {formErrors.global && (
        <KiteAlert
          description={formErrors.global}
          type="alert"
          className="same-underlay__alert"
        />
      )}

      <SameUnderlaySectionCard
        underlayForms={underlayForms}
        formErrors={formErrors}
        underlayProducts={underlayProducts}
        onAddUnderlay={handleAddUnderlay}
        onDeleteUnderlay={handleDeleteUnderlay}
        onUnderlayChange={handleUnderlayChange}
        maxUnderlays={MAX_UNDERLAYS}
        globalError={globalError}
        isLoading={isLoadingUnderlayProducts || locationsLoading}
        term={term}
        noOfSites={noOfSites}
      />
    </>
  );
};

export default ApplySameUnderlayToAll;
